import {
  Dialog,
  FormControlLabel,
  IconButton,
  makeStyles,
  Radio as MuiRadio,
  RadioGroup,
  RadioProps,
  Typography,
} from "@material-ui/core"
import clsx from "clsx"
import dayjs from "dayjs"
import { useQueryClient } from "@tanstack/react-query"
import { FC, useState } from "react"
import {
  useDeleteNudgeScheduleByIdMutation,
  useDeleteQuestionnaireScheduleByIdMutation,
  useInsertBlackoutDateMutation,
} from "../../generated/graphql"
import { useGraphQLClient } from "../../hooks/useGraphQLClient"
import { isNudgeSchedule } from "../../utils/helpers"
import { colors } from "../../utils/theme"
import { ScheduleItem } from "../../utils/types"
import Button from "../atoms/button/Button"
import CloseIcon from "../atoms/CloseIcon"
import RadioCheckedIcon from "../atoms/RadioCheckedIcon"
import RadioUncheckedIcon from "../atoms/RadioUncheckIcon"

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    border: `1px solid ${colors.neutral.clay}`,
    boxShadow: "none",
  },
  dialogCloseContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    padding: theme.spacing(2, 2, 1, 2),
  },
  dialogContent: {
    padding: theme.spacing(0, 5, 5, 5),
  },
  closeButton: {
    margin: 0,
    padding: 0,
    border: 0,
    height: 16,
    width: 16,
  },
  dialogTitle: {
    color: colors.neutral.denim,
  },
  dialogButton: {
    width: "100%",
  },
  radioLabel: {
    color: colors.neutral.denim,
  },
  firstButton: {
    marginBottom: theme.spacing(2),
  },
  dialogText: {
    color: colors.neutral.denim,
    marginTop: 12,
    marginBottom: theme.spacing(4),
  },
  radios: {
    marginTop: theme.spacing(5),
    marginBottom: theme.spacing(3),
  },
  radio: {
    "&&:hover": {
      backgroundColor: "transparent",
    },
  },
}))

const Radio: FC<RadioProps> = (props) => {
  const classes = useStyles()
  return (
    <MuiRadio
      {...props}
      disableRipple={true}
      checkedIcon={<RadioCheckedIcon />}
      icon={<RadioUncheckedIcon />}
      className={classes.radio}
    />
  )
}

const DeleteScheduleDialog: FC<{
  open: boolean
  onClose: () => void
  item: ScheduleItem
  currentDate?: dayjs.Dayjs
  insufficientPermissions: (check: boolean) => void
}> = ({ open, onClose, item, currentDate, insufficientPermissions }) => {
  const classes = useStyles()
  const [recurringValue, setRecurringValue] = useState<
    "one" | "following" | string
  >("one")
  const graphqlClient = useGraphQLClient()
  const queryClient = useQueryClient()
  const onSuccess = (data: any) => {
    if (
      data?.delete_questionnaire_schedules_by_pk === null ||
      data?.delete_nudge_schedules_by_pk === null
    ) {
      insufficientPermissions(true)
      return
    }
    queryClient.invalidateQueries({ queryKey: ["NudgesScheduleByUser"] })
    queryClient.invalidateQueries({
      queryKey: ["QuestionnairesScheduleByUser"],
    })
  }
  const { mutate: deleteQuestionnaireSchedule } =
    useDeleteQuestionnaireScheduleByIdMutation(graphqlClient, { onSuccess })
  const { mutate: deleteNudgeSchedule } = useDeleteNudgeScheduleByIdMutation(
    graphqlClient,
    { onSuccess }
  )
  const { mutate: insertBlackoutDate } = useInsertBlackoutDateMutation(
    graphqlClient,
    {
      onSuccess,
      onError: () => {
        console.error("Failed to insert blackout date")
        insufficientPermissions(true)
      },
    }
  )

  const updateSchedule = async () => {
    if (
      item.one_time ||
      (recurringValue === "following" &&
        dayjs(currentDate).format("YYYY-MM-DD") ===
          dayjs(item.scheduled_at).format("YYYY-MM-DD"))
    ) {
      if (isNudgeSchedule(item)) {
        deleteNudgeSchedule({ id: item.id })
      } else {
        deleteQuestionnaireSchedule({ id: item.id })
      }
    } else {
      insertBlackoutDate({
        blackout_date: dayjs(currentDate).format("YYYY-MM-DD"),
        blackout_following: recurringValue === "following",
        ...(isNudgeSchedule(item)
          ? { nudge_schedules_id: item.id }
          : { questionnaire_schedules_id: item.id }),
      })
    }
    onClose()
  }

  const type = isNudgeSchedule(item) ? "Nudge" : "Assessment"

  return (
    <Dialog
      fullWidth={true}
      maxWidth="xs"
      open={open}
      onClose={onClose}
      classes={{
        paper: classes.dialogPaper,
      }}
    >
      <div className={classes.dialogCloseContainer}>
        <IconButton className={classes.closeButton} onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </div>
      <div className={classes.dialogContent}>
        <Typography variant="h2" className={classes.dialogTitle}>
          {item.one_time ? `Delete ${type}` : `Delete Recurring ${type}`}
        </Typography>
        {item.one_time ? (
          <Typography variant="body1" className={classes.dialogText}>
            {`Are you sure you want to permanently delete this one time ${type.toLowerCase()}?`}
          </Typography>
        ) : (
          <RadioGroup
            aria-label="recurring-or-not"
            name="recurring"
            value={recurringValue}
            onChange={(event) => setRecurringValue(event.target.value)}
            className={classes.radios}
          >
            <FormControlLabel
              value="one"
              control={<Radio />}
              label={`This ${type.toLowerCase()}`}
              classes={{
                label: classes.radioLabel,
              }}
            />
            <FormControlLabel
              value="following"
              control={<Radio />}
              label={`This and following ${type.toLowerCase()}s`}
              classes={{
                label: classes.radioLabel,
              }}
            />
          </RadioGroup>
        )}
        <Button
          customVariant="primary"
          className={clsx(classes.dialogButton, classes.firstButton)}
          onClick={updateSchedule}
        >
          Confirm
        </Button>
        <Button
          customVariant="ghost"
          className={classes.dialogButton}
          onClick={onClose}
        >
          Cancel
        </Button>
      </div>
    </Dialog>
  )
}

export default DeleteScheduleDialog
